import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import {
  Card,
  CardContent,
  Badge,
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@/shared/components/ui";
import { getStatusVariant, getStatusLabel, formatDateTime } from "@/shared/lib/utils";
import {
  Users,
  CheckCircle,
  XCircle,
  Plus,
  Loader2,
  AlertCircle,
  Search,
} from "lucide-react";
import type { Advisor } from "@/shared/types";

export function AdminAdvisorsPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedAdvisor, setSelectedAdvisor] = useState<Advisor | null>(null);
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState(10);
  const [creditsReason, setCreditsReason] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-advisors"],
    queryFn: () => api.getAdvisors(),
  });

  const verifyMutation = useMutation({
    mutationFn: (advisorId: string) => api.verifyAdvisor(advisorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-advisors"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
    },
  });

  const suspendMutation = useMutation({
    mutationFn: (advisorId: string) => api.suspendAdvisor(advisorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-advisors"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
    },
  });

  const assignCreditsMutation = useMutation({
    mutationFn: ({ advisorId, amount, reason }: { advisorId: string; amount: number; reason?: string }) =>
      api.assignCredits(advisorId, amount, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-advisors"] });
      setCreditsDialogOpen(false);
      setSelectedAdvisor(null);
      setCreditsAmount(10);
      setCreditsReason("");
    },
  });

  const advisors = (data?.advisors || []) as Advisor[];

  // Filter advisors
  const filteredAdvisors = advisors.filter((advisor) => {
    const matchesSearch =
      advisor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      advisor.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      advisor.company?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === "all" || advisor.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleOpenCreditsDialog = (advisor: Advisor) => {
    setSelectedAdvisor(advisor);
    setCreditsDialogOpen(true);
  };

  const handleAssignCredits = () => {
    if (selectedAdvisor) {
      assignCreditsMutation.mutate({
        advisorId: selectedAdvisor.id,
        amount: creditsAmount,
        reason: creditsReason || undefined,
      });
    }
  };

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
        <p className="text-gray-600">Nie udało się pobrać listy doradców</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Doradcy</h1>
          <p className="text-gray-600 mt-1">
            Zarządzaj kontami doradców ({filteredAdvisors.length})
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Szukaj doradcy..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex gap-2">
              {["all", "pending", "active", "suspended"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {status === "all"
                    ? "Wszyscy"
                    : status === "pending"
                      ? "Oczekujący"
                      : status === "active"
                        ? "Aktywni"
                        : "Zawieszeni"}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advisors List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : filteredAdvisors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Brak doradców spełniających kryteria</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAdvisors.map((advisor) => (
            <Card key={advisor.id}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{advisor.name}</h3>
                      <p className="text-sm text-gray-600">{advisor.email}</p>
                      {advisor.company && (
                        <p className="text-sm text-gray-500">{advisor.company}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Kredyty</p>
                      <p className="text-xl font-bold text-primary-600">{advisor.credits}</p>
                    </div>

                    <Badge variant={getStatusVariant(advisor.status)}>
                      {getStatusLabel(advisor.status)}
                    </Badge>

                    <div className="flex items-center gap-2">
                      {advisor.status === "pending" && (
                        <Button
                          size="sm"
                          onClick={() => verifyMutation.mutate(advisor.id)}
                          isLoading={verifyMutation.isPending}
                          leftIcon={<CheckCircle className="w-4 h-4" />}
                        >
                          Zatwierdź
                        </Button>
                      )}

                      {advisor.status === "active" && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => suspendMutation.mutate(advisor.id)}
                          isLoading={suspendMutation.isPending}
                          leftIcon={<XCircle className="w-4 h-4" />}
                        >
                          Zawieś
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenCreditsDialog(advisor)}
                        leftIcon={<Plus className="w-4 h-4" />}
                      >
                        Kredyty
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex gap-6 text-sm text-gray-600">
                  <span>ID: {advisor.id.slice(0, 8)}...</span>
                  <span>Zarejestrowany: {formatDateTime(advisor.createdAt)}</span>
                  {advisor.phone && <span>Tel: {advisor.phone}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Assign Credits Dialog */}
      <Dialog open={creditsDialogOpen} onOpenChange={setCreditsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Przyznaj kredyty</DialogTitle>
            <DialogDescription>
              Przyznaj kredyty dla: {selectedAdvisor?.name}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Obecna liczba kredytów</p>
              <p className="text-2xl font-bold text-primary-600">
                {selectedAdvisor?.credits || 0}
              </p>
            </div>

            <Input
              label="Liczba kredytów do przyznania"
              type="number"
              min="1"
              value={creditsAmount}
              onChange={(e) => setCreditsAmount(Number(e.target.value))}
            />

            <Input
              label="Powód (opcjonalnie)"
              placeholder="np. Bonus powitalny"
              value={creditsReason}
              onChange={(e) => setCreditsReason(e.target.value)}
            />

            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Po operacji:</p>
              <p className="text-lg font-semibold">
                {(selectedAdvisor?.credits || 0) + creditsAmount} kredytów
              </p>
            </div>

            {assignCreditsMutation.error && (
              <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                <p className="text-sm text-error-700">
                  {(assignCreditsMutation.error as Error).message}
                </p>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreditsDialogOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={handleAssignCredits}
              isLoading={assignCreditsMutation.isPending}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Przyznaj kredyty
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
